import { useState, useCallback } from 'react';
import GlassCard from '../../common/GlassCard/GlassCard';
import styles from './Portfolio.module.css';

const projects = [
  {
    id: 1,
    category: 'Web App / UX·UI Design',
    title: 'GIGiFY',
    description: 'A secure & automated Talent Booking & Management suite. GIGiFY innovates through a role-based architecture that orchestrates the complete booking lifecycle — from talent discovery and calendar management to automated contract generation, annexured riders, and escrow payment integration that protects both performer and organiser at every stage.',
    tags: ['React', 'Firebase', 'Node.js'],
    orientation: 'landscape',
    images: [
      '/images/portfolio/GigifyLandingPage.png',
      '/images/portfolio/GigifyLogin.png',
      '/images/portfolio/GigifyProfile.png',
      '/images/portfolio/GigifyTechnicalRider.png',
      '/images/portfolio/GigifyFeatures.png',
    ],
  },
  {
    id: 2,
    category: 'Web App / Mobile App',
    title: 'FlowVerse',
    description: 'FlowVerse is an AI-powered field sales and service platform built for South African distributors and on-the-road teams. Reps work from a native mobile app to capture orders, log GPS-verified customer visits, and follow AI-optimised routes between stops. Managers get a live web dashboard for territory management, performance tracking, and prospecting. The platform\'s intelligent architecture quietly adapts between distribution, field-service, and hybrid business models, delivering enterprise-grade role controls and data isolation at a fraction of the traditional cost.',
    tags: ['AI Route Optimisation', 'React', 'Offline-First Sync Engine'],
    orientation: 'landscape',
    images: [
      '/images/portfolio/FlowverseComp.png',
      '/images/portfolio/FlowverseLanding.png',
      '/images/portfolio/FlowverseTools.png',
      '/images/portfolio/FlowverseResults.png',
    ],
  },
  {
    id: 3,
    category: 'Web App / AI',
    title: 'CogniPrint',
    description: 'CogniPrint is an AI-powered adaptive learning platform built on a simple idea: people don\'t all learn the same way. A short diagnostic builds a personal learning profile across 8 cognitive dimensions, and a four-stage AI engine reshapes uploaded study material into a learning path tuned specifically to you. An interactive AI tutor tailors your content, able to go deeper, test comprehension, connect ideas, or simplify on demand. Content isn\'t just summarised, it\'s restructured around how your brain actually works.',
    tags: ['4-Pass AI Architecture', 'Knowledge Graph Engine', 'Tailwind CSS'],
    orientation: 'landscape',
    images: [
      '/images/portfolio/CogniprintComp.png',
      '/images/portfolio/CogniprintLanding.png',
      '/images/portfolio/CogniprintDashboard.png',
    ],
  },
  {
    id: 4,
    category: 'Web App / SaaS',
    title: 'AlumniVerse',
    description: 'AlumniVerse is a purpose-built alumni engagement platform that reconnects educational institutional graduates with their graduates for life. A rich profile system lets alumni showcase careers, achievements, and volunteer interests across a searchable directory, while structured community groups, one-on-one mentorship pairings, and a live jobs board transform passive alumni lists into an active, generational network. The platform uniquely channels institutional revenue into student bursaries, making alumni engagement a direct pipeline of opportunity for current and prospective future learners.',
    tags: ['Alumni SaaS', 'POPIA Compliant', 'Mentorship & Gamification'],
    orientation: 'landscape',
    images: [
      '/images/portfolio/AlumniverseComp.png',
      '/images/portfolio/AlumniverseLanding.png',
    ],
  },
];

const Portfolio = () => {
  const [activeProject, setActiveProject] = useState(0);
  const [imageIndices, setImageIndices] = useState({});

  const prevProject = useCallback(() => {
    setActiveProject(i => (i - 1 + projects.length) % projects.length);
  }, []);

  const nextProject = useCallback(() => {
    setActiveProject(i => (i + 1) % projects.length);
  }, []);

  const prevImage = useCallback((project) => {
    setImageIndices(prev => {
      const cur = prev[project.id] ?? 0;
      return { ...prev, [project.id]: (cur - 1 + project.images.length) % project.images.length };
    });
  }, []);

  const nextImage = useCallback((project) => {
    setImageIndices(prev => {
      const cur = prev[project.id] ?? 0;
      return { ...prev, [project.id]: (cur + 1) % project.images.length };
    });
  }, []);

  const goToImage = useCallback((project, iIdx) => {
    setImageIndices(prev => ({ ...prev, [project.id]: iIdx }));
  }, []);

  return (
    <section className={styles.section} id="portfolio">

      <div className={styles.gradientBackground} />
      <div className="section-label">Our Work</div>

      <div
        className={styles.track}
        style={{ transform: `translateX(-${activeProject * 100}vw)` }}
      >
        {projects.map((project) => (
          <div key={project.id} className={styles.slide}>
            <GlassCard padding="none" className={styles.card}>

              {/* ── Left column: image only ── */}
              <div className={styles.leftColumn}>
                <div className={`${styles.imagePanel} ${project.orientation === 'portrait' ? styles.imagePanelPortrait : ''}`}>
                  {project.images.map((src, iIdx) => (
                    <img
                      key={iIdx}
                      src={src}
                      alt={`${project.title} — screenshot ${iIdx + 1}`}
                      className={`${styles.projectImage} ${(imageIndices[project.id] ?? 0) === iIdx ? styles.imageActive : ''}`}
                    />
                  ))}
                  <button
                    className={`${styles.arrow} ${styles.arrowLeft}`}
                    onClick={() => prevImage(project)}
                    aria-label="Previous image"
                  >←</button>
                  <button
                    className={`${styles.arrow} ${styles.arrowRight}`}
                    onClick={() => nextImage(project)}
                    aria-label="Next image"
                  >→</button>
                  <div className={styles.imageDots}>
                    {project.images.map((_, iIdx) => (
                      <button
                        key={iIdx}
                        className={`${styles.imageDot} ${(imageIndices[project.id] ?? 0) === iIdx ? styles.imageDotActive : ''}`}
                        onClick={() => goToImage(project, iIdx)}
                        aria-label={`Image ${iIdx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Right column: project info + project nav ── */}
              <div className={styles.infoPanel}>
                <div className={styles.infoPanelInner}>
                  <span className={styles.category}>{project.category}</span>
                  <h2 className={styles.title}>{project.title}</h2>
                  <p className={styles.description}>{project.description}</p>
                  <div className={styles.tags}>
                    {project.tags.map(tag => (
                      <span key={tag} className={styles.tag}>{tag}</span>
                    ))}
                  </div>
                </div>
                <div className={styles.navRow}>
                  <div className={styles.navArrows}>
                    <button className={styles.navArrow} onClick={prevProject} aria-label="Previous project">
                      ← Previous
                    </button>
                    <button className={styles.navArrow} onClick={nextProject} aria-label="Next project">
                      Next →
                    </button>
                  </div>
                  <div className={styles.counter}>
                    <div className={styles.counterNumbers}>
                      <span className={styles.counterCurrent}>{String(activeProject + 1).padStart(2, '0')}</span>
                      <span className={styles.counterSeparator}> / </span>
                      <span className={styles.counterTotal}>{String(projects.length).padStart(2, '0')}</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${(activeProject / Math.max(projects.length - 1, 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

            </GlassCard>
          </div>
        ))}
      </div>

    </section>
  );
};

export default Portfolio;
